
import type { PropType } from 'vue'
import { defineComponent, ref, toRefs, watch } from 'vue'
import type { RawLocation } from 'vue-router'
import { useInfiniteScroll } from '@vueuse/core'
import {
  WeveButtonIcon,
  WeveTextField,
  WeveSpinnerContainer,
  WeveSpinner,
} from '@weve/ui'

import { Nullable, Status, TagName } from '@/types'
import { useSyncProp } from '@/use'
import {
  ChevronRightIcon,
  ChevronDownIcon,
  PlusIcon,
  MagnifyIcon,
  FilterIcon,
} from '@icons'
import StatusSelect from '@/components/StatusSelect/StatusSelect.vue'
import TagsMultiselect from '@/components/TagsMultiselect/TagsMultiselect.vue'

import SidebarSectionHeader from './SidebarSectionHeader.vue'

export default defineComponent({
  name: 'BaseSidebarSection',
  components: {
    WeveButtonIcon,
    WeveTextField,
    WeveSpinnerContainer,
    WeveSpinner,
    ChevronRightIcon,
    ChevronDownIcon,
    PlusIcon,
    MagnifyIcon,
    FilterIcon,
    StatusSelect,
    TagsMultiselect,
    SidebarSectionHeader,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    open: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    query: {
      type: String,
      default: undefined,
    },
    status: {
      type: String as PropType<Status>,
      default: undefined,
      validator: (val: Status) => Object.values(Status).includes(val),
    },
    tags: {
      type: Array as PropType<TagName[]>,
      default: () => [],
    },
    to: {
      type: Object as PropType<RawLocation>,
      default: null,
    },
  },
  emits: {
    toggle: null,
    create: null,
    more: null,
    'update:query': (value: string) => typeof value === 'string',
    'update:status': (value: Status) => Object.values(Status).includes(value),
    'update:tags': (value: TagName[]) => Array.isArray(value),
  },
  setup(props, { emit }) {
    const { query, status, tags } = toRefs(props)
    const isSearchActive = ref(false)
    const query$ = useSyncProp(query, 'query', emit)
    const status$ = useSyncProp(status, 'status', emit)
    const tags$ = useSyncProp(tags, 'tags', emit)
    const isFilterActive = ref(false)

    watch(
      tags$,
      (value) => {
        if (value == null) return
        if (value.length < 1) return
        if (isFilterActive.value) return
        isFilterActive.value = true
      },
      { deep: true }
    )

    function toggleSearch() {
      isSearchActive.value = !isSearchActive.value
    }

    function toggleFilter() {
      isFilterActive.value = !isFilterActive.value
    }

    function toggle() {
      emit('toggle')
    }

    function create() {
      emit('create')
    }

    const scroller = ref<Nullable<HTMLElement>>(null)

    useInfiniteScroll(scroller, () => {
      emit('more')
    })

    return {
      isSearchActive,
      query$,
      status$,
      tags$,
      toggleSearch,
      isFilterActive,
      toggleFilter,
      toggle,
      create,
      scroller,
    }
  },
})
